import React, { useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Divider,
  Stack,
  Chip,
  Grid,
} from "@mui/material";
import {
  Gavel as GavelIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Headphones as HeadphonesIcon,
} from "@mui/icons-material";
import { Link } from "react-router-dom";

const TermsOfUse = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", display: "flex", flexDirection: "column" }}>
      {/* LIGHT HEADER BANNER */}
      <Box
        sx={{
          bgcolor: "#f0f7ff",
          borderBottom: "1px solid #e2e8f0",
          py: { xs: 5, md: 6 },
          background: "linear-gradient(135deg, #f0f7ff 0%, #e0f2fe 100%)",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <Chip
              icon={<GavelIcon sx={{ fontSize: "16px !important", color: "#0284c7 !important" }} />}
              label="LEGAL AGREEMENT"
              size="small"
              sx={{
                bgcolor: "#ffffff",
                color: "#0284c7",
                fontWeight: 700,
                fontSize: "0.75rem",
                letterSpacing: "0.05em",
                border: "1px solid #bae6fd",
              }}
            />
            <Chip
              icon={<BusinessIcon sx={{ fontSize: "16px !important", color: "#64748b !important" }} />}
              label="Bridgeme Technologies Private Limited"
              size="small"
              sx={{
                bgcolor: "#ffffff",
                color: "#475569",
                fontSize: "0.75rem",
                border: "1px solid #e2e8f0",
              }}
            />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.03em",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "2.75rem" },
              mb: 1.5,
              lineHeight: 1.2,
            }}
          >
            Terms of Use
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#475569",
              fontSize: { xs: "0.95rem", md: "1.05rem" },
              lineHeight: 1.7,
              maxWidth: 700,
            }}
          >
            Please read these Terms carefully before creating an account, accessing, or using PulseHR.
          </Typography>
        </Container>
      </Box>

      {/* MAIN CONTENT CONTAINER */}
      <Box sx={{ flex: 1, py: { xs: 4, md: 6 } }}>
        <Container maxWidth="md">
          {/* INTRO */}
          <Box sx={{ mb: 4 }}>
            <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, fontSize: "1rem", mb: 2 }}>
              Welcome to <strong>PulseHR</strong>, a Human Resource Management platform provided by{" "}
              <strong>Bridgeme Technologies Private Limited</strong> ("Bridgeme Technologies", "Company", "we", "us", or "our").
            </Typography>
            <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, fontSize: "1rem" }}>
              These Terms of Use govern your access to and use of PulseHR. Please read these Terms carefully before creating an account, accessing, or using PulseHR.
            </Typography>

            {/* ACCEPTANCE CALLOUT */}
            <Box
              sx={{
                p: 3,
                my: 3.5,
                borderRadius: 2,
                bgcolor: "#f0f9ff",
                border: "1px solid #bae6fd",
                borderLeft: "4px solid #0284c7",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <InfoIcon sx={{ color: "#0284c7", fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#0369a1", fontSize: "1.05rem" }}>
                  Acceptance of Agreement
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: "#0f172a", lineHeight: 1.75, mb: 1 }}>
                You agree to the terms and conditions outlined in this Terms of Use Agreement ("Agreement" or "Terms").
              </Typography>
              <Typography variant="body2" sx={{ color: "#0f172a", lineHeight: 1.75, mb: 1 }}>
                By creating an account, clicking "I Agree", logging in, accessing, or using PulseHR, you acknowledge that you have read, understood, and agreed to be bound by these Terms.
              </Typography>
              <Typography variant="body2" sx={{ color: "#0f172a", lineHeight: 1.75, mb: 1 }}>
                If you are using PulseHR on behalf of an organization, you confirm that you are authorized to accept these Terms on behalf of that organization.
              </Typography>
              <Typography variant="body2" sx={{ color: "#b91c1c", fontWeight: 700, lineHeight: 1.75, mt: 1.5 }}>
                If you do not agree to these Terms, you must not access or use PulseHR.
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* SECTIONS */}
          <Stack spacing={4.5} sx={{ color: "#334155" }}>
            {/* PulseHR Service */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                PulseHR Service
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 2 }}>
                PulseHR is a Human Resource Management platform that provides features such as:
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  "Employee management",
                  "Recruitment and onboarding",
                  "Attendance",
                  "Leave management",
                  "Performance management",
                  "Training",
                  "Project management",
                  "Tax and statutory information",
                  "Allowances and claims",
                  "Employee records and documents",
                  "Reports and HR workflows",
                ].map((item, idx) => (
                  <Grid item xs={12} sm={6} key={idx}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1.25,
                        borderRadius: 1.5,
                        bgcolor: "#ffffff",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: 16, color: "#10b981" }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155" }}>
                        {item}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="body2" sx={{ color: "#64748b", mt: 2, fontStyle: "italic" }}>
                The features available to you may depend on your selected plan or subscription.
              </Typography>
            </Box>

            <Divider />

            {/* Access and License */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Access and License
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Bridgeme Technologies grants the Customer a limited, non-exclusive, non-transferable right to access and use PulseHR for its internal business and HR operations during the applicable subscription or authorized period.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Access is limited to authorized users of the Customer's organization.
              </Typography>
              <Typography variant="body1" sx={{ color: "#0f172a", lineHeight: 1.8, fontWeight: 600 }}>
                No ownership of PulseHR or its software is transferred to the Customer.
              </Typography>
            </Box>

            <Divider />

            {/* Customer Account */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Customer Account
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                The Customer is responsible for:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Providing accurate account and organization information.</li>
                <li>Keeping login credentials secure.</li>
                <li>Managing user access and permissions.</li>
                <li>Removing access for users who are no longer authorized.</li>
                <li>Ensuring that authorized users comply with these Terms.</li>
              </Box>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mt: 1.5 }}>
                The Customer is responsible for activities performed through its accounts.
              </Typography>
            </Box>

            <Divider />

            {/* Customer Data */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Customer Data
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                The Customer retains ownership of the business and employee information entered into PulseHR.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                The Customer is responsible for ensuring that information provided to PulseHR is accurate, lawful, and collected with any required permissions or consents.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                Bridgeme Technologies may process Customer Data as necessary to provide, maintain, secure, and support PulseHR, subject to applicable agreements and the Privacy Policy.
              </Typography>
            </Box>

            <Divider />

            {/* AI and Automated Features */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                AI and Automated Features
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                PulseHR may include AI-powered or automated features such as document processing, summaries, recommendations, analytics, and workflow automation.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Automated and AI-generated results may contain errors or inaccuracies and are provided for assistance only.
              </Typography>
              <Typography variant="body1" sx={{ color: "#0f172a", lineHeight: 1.8, fontWeight: 700 }}>
                The Customer is responsible for reviewing such results and making final decisions.
              </Typography>
            </Box>

            <Divider />

            {/* Acceptable Use */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Acceptable Use
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                You must not use PulseHR to:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Violate applicable laws or regulations.</li>
                <li>Gain unauthorized access to accounts, systems, or data.</li>
                <li>Upload malicious software or harmful content.</li>
                <li>Interfere with or disrupt the service.</li>
                <li>Circumvent security, access, subscription, or usage restrictions.</li>
                <li>Use another customer's account or data without authorization.</li>
                <li>Use the service for fraudulent or unlawful activities.</li>
              </Box>
            </Box>

            <Divider />

            {/* Intellectual Property */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Intellectual Property
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                PulseHR, including its software, source code, architecture, database structures, user interface, designs, workflows, documentation, trademarks, logos, and other related materials, belongs to or is licensed to <strong>Bridgeme Technologies Private Limited</strong>.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                The Customer receives only the right to use PulseHR according to these Terms and the applicable subscription.
              </Typography>
            </Box>

            <Divider />

            {/* Restrictions */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Restrictions
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                You must not:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>Reverse engineer, decompile, disassemble, or attempt to discover the source code or underlying technology of PulseHR.</li>
                <li>Copy, reproduce, modify, or create derivative works of PulseHR.</li>
                <li>Attempt to reproduce or replicate the software or its underlying functionality.</li>
                <li>Sell, rent, lease, sublicense, or redistribute PulseHR.</li>
                <li>Provide unauthorized access to PulseHR to third parties.</li>
                <li>Bypass or disable security, licensing, subscription, or usage controls.</li>
              </Box>
            </Box>

            <Divider />

            {/* Security and Audit Logs */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Security and Audit Logs
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                PulseHR may maintain security and audit records relating to the use of the service.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                These records may include account information, administrative actions, application activity, license information, and other technical information where applicable.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Such information may be used for security, troubleshooting, service administration, auditing, and compliance purposes.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                The processing of personal information is subject to the applicable Privacy Policy.
              </Typography>
            </Box>

            <Divider />

            {/* Service Availability */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Service Availability
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                We make reasonable efforts to keep PulseHR secure and available.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                However, PulseHR may occasionally be unavailable due to maintenance, technical issues, security incidents, network problems, or circumstances beyond our reasonable control.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                We do not guarantee that the service will always be uninterrupted or error-free.
              </Typography>
            </Box>

            <Divider />

            {/* Suspension and Termination */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Suspension and Termination
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Bridgeme Technologies may suspend or restrict access to PulseHR if:
              </Typography>
              <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 0.75, color: "#334155", lineHeight: 1.7 } }}>
                <li>These Terms are materially violated.</li>
                <li>Unauthorized or fraudulent activity is detected.</li>
                <li>There is a security risk.</li>
                <li>Applicable subscription fees are not paid.</li>
                <li>The subscription or authorized access has expired or been terminated.</li>
              </Box>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mt: 1.5 }}>
                When access ends, the Customer's right to use PulseHR will also end, subject to any applicable subscription or customer agreement.
              </Typography>
            </Box>

            <Divider />

            {/* Disclaimer */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Disclaimer
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                PulseHR is provided on an <strong>"AS IS"</strong> and <strong>"AS AVAILABLE"</strong> basis to the maximum extent permitted by law.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                Bridgeme Technologies does not guarantee that PulseHR will always be uninterrupted, error-free, completely accurate, or suitable for every specific business requirement.
              </Typography>
            </Box>

            <Divider />

            {/* Limitation of Liability */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Limitation of Liability
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                To the maximum extent permitted by applicable law, Bridgeme Technologies and its directors, officers, employees, and affiliates will not be liable for indirect, incidental, special, consequential, or similar losses arising from the use of or inability to use PulseHR.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Bridgeme Technologies is not responsible for any business decisions, actions, or consequences arising from the use of PulseHR, including AI-generated predictions, insights, recommendations, or other outputs.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                Nothing in these Terms excludes or limits liability that cannot legally be excluded or limited.
              </Typography>
            </Box>

            <Divider />

            {/* Changes to the Service */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Changes to the Service
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                Bridgeme Technologies may update, modify, add, or remove features of PulseHR from time to time.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 1.5 }}>
                We may also update these Terms when necessary.
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8 }}>
                The latest version will be made available through PulseHR or the applicable website.
              </Typography>
            </Box>

            <Divider />

            {/* Contact Us */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a", fontSize: "1.15rem", mb: 1.5 }}>
                Contact Us
              </Typography>
              <Typography variant="body1" sx={{ color: "#334155", lineHeight: 1.8, mb: 2 }}>
                If you have any questions or concerns regarding these Terms or your use of PulseHR, you may contact <strong>Bridgeme Technologies Private Limited</strong>:
              </Typography>
              <Box sx={{ p: 3, bgcolor: "#ffffff", borderRadius: 2.5, border: "1px solid #e2e8f0", mb: 2.5, boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a", mb: 0.5 }}>
                  Bridgeme Technologies Private Limited
                </Typography>
                <Typography variant="body2" sx={{ color: "#64748b", mb: 2.5 }}>
                  <strong>Product:</strong> PulseHR
                </Typography>

                <Divider sx={{ mb: 2.5 }} />

                <Stack spacing={2.5}>
                  {/* Call Us */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <PhoneIcon sx={{ color: "#ea580c", fontSize: 26, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        Call Us
                      </Typography>
                      <Typography
                        component="a"
                        href="tel:+919482673071"
                        variant="body2"
                        sx={{ color: "#334155", mt: 0.5, display: "inline-block", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#ea580c" } }}
                      >
                        +91 948 267 3071
                      </Typography>
                    </Box>
                  </Box>

                  {/* Email Us */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <EmailIcon sx={{ color: "#ea580c", fontSize: 26, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        Email Us
                      </Typography>
                      <Typography
                        component="a"
                        href="mailto:info@bridgemetechnologies.com"
                        variant="body2"
                        sx={{ color: "#334155", mt: 0.5, display: "inline-block", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#ea580c" } }}
                      >
                        info@bridgemetechnologies.com
                      </Typography>
                    </Box>
                  </Box>

                  {/* Customer Support */}
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                    <HeadphonesIcon sx={{ color: "#ea580c", fontSize: 26, mt: 0.2 }} />
                    <Box>
                      <Typography variant="body1" sx={{ fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        Customer Support
                      </Typography>
                      <Typography
                        component="a"
                        href="mailto:info@bridgemetechnologies.com"
                        variant="body2"
                        sx={{ color: "#334155", mt: 0.5, display: "inline-block", textDecoration: "none", fontWeight: 500, "&:hover": { color: "#ea580c" } }}
                      >
                        info@bridgemetechnologies.com
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Box>
              <Box sx={{ p: 2.5, bgcolor: "#ffffff", borderRadius: 2, border: "1px solid #e2e8f0" }}>
                <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                  By continuing to access or use PulseHR, you acknowledge that you have read, understood, and agreed to these Terms of Use.
                </Typography>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>

      {/* FOOTER */}
      <Box sx={{ bgcolor: "#ffffff", borderTop: "1px solid #e2e8f0", py: 3, mt: "auto" }}>
        <Container maxWidth="md">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            spacing={2}
          >
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              © {new Date().getFullYear()} PulseHR · Bridgeme Technologies Private Limited
            </Typography>
            <Stack direction="row" spacing={3}>
              <Link
                to="/privacy"
                style={{ textDecoration: "none", color: "#64748b", fontSize: "0.875rem", fontWeight: 500 }}
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                style={{ textDecoration: "none", color: "#0284c7", fontSize: "0.875rem", fontWeight: 600 }}
              >
                Terms of Use
              </Link>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default TermsOfUse;
